/** `aux_cla.cla_ext` is a `varchar(25)`. */
export const EXTERNAL_CODE_MAX_LENGTH = 25;

/**
 * The AFAS benefit import matches `cla_ext` as a '#'-delimited set
 * (`lower(aux_cla.cla_ext) like lower('%#' || imp_benefit_hor.cla_imp || '#%')`,
 * `ImportsBenefitMapper.xml`), so a code entered here is stored between '#'s.
 * Splitting on '#' first keeps the result idempotent and drops the empty parts
 * that a stray delimiter (`14##15`, `#`) would otherwise leave behind.
 *
 * Note that only '#' separates codes: `14 15` stays one code, because guessing
 * a second separator would silently turn one code into two.
 */
export const normalizeExternalCode = (value: string): string | undefined => {
  const parts = value
    .split('#')
    .map((part) => part.trim())
    .filter(Boolean);

  return parts.length ? `#${parts.join('#')}#` : undefined;
};

/**
 * The value to send for `externalCode`, given what the user has in the field
 * and what the backend returned for this row.
 *
 * A value the user did not change is sent back exactly as it was stored. Plain
 * values do exist — `AfasMigration.migrateClas` writes the import code without
 * delimiters — and `AfasEmployeeProvider.groovy` indexes CLAs on the exact
 * `claExt`, so rewriting `14` into `#14#` while saving an unrelated field would
 * break that lookup and with it the hourly-wage-to-period-salary conversion in
 * `AfasBenefitAdapter`. Normalising only what the user actually typed keeps that
 * decision with the person making it.
 *
 * An empty field yields `undefined`, so a row whose `cla_ext` is NULL stays NULL
 * instead of becoming `''` on its first save.
 */
export const externalCodeToSave = (
  value: string | null | undefined,
  storedValue: string | null | undefined
): string | undefined => {
  const trimmed = value?.trim() ?? '';

  if (!trimmed) return undefined;
  if (trimmed === storedValue) return trimmed;

  return normalizeExternalCode(trimmed);
};
